import { useState, useEffect, useRef, useCallback } from 'react';
import { getWsToken } from '../lib/api.js';

const MAX_BACKOFF = 60000;
const BASE_DELAY = 2000;
const MAX_RETRIES = 10;

/**
 * WebSocket hook — subscribes to the realtime relay.
 *
 * Pass an agentId for ONE host's feed. Pass nothing (or null) for the ORG feed:
 * every host on one socket, each event tagged `agent_id` / `agent_name` by
 * OrgRelay. The org feed is what the dashboard uses — subscribing to a single
 * agent meant Live Calls covered whichever agent sorted first and silently
 * ignored every other PBX (found 2026-08-02, a real call on PBX-02 showed
 * "0 active").
 * Event types the agent emits (see cloud/workers/src/durable/AgentRelay.js
 * VALID_EVENT_TYPES): agent.status, registration.up/down, call.state_change,
 * cdr.written. The hook doesn't interpret them — it just publishes lastEvent;
 * AppShell dispatches to TanStack Query invalidation.
 */
export function useRealtime(agentId = null) {
  const [connectionState, setConnectionState] = useState('disconnected'); // 'connecting' | 'connected' | 'disconnected'
  const [agentStatus, setAgentStatus] = useState('unknown'); // 'online' | 'offline' | 'unknown'
  // Org feed only: agent_id → 'online' | 'offline'. One host being down is not the
  // org being down, so per-host state is kept apart from `agentStatus`.
  const [hostStatus, setHostStatus] = useState({});
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const timerRef = useRef(null);
  const everConnectedRef = useRef(false);

  const connect = useCallback(function connect() {

    // Stop retrying after MAX_RETRIES if we never successfully connected.
    if (!everConnectedRef.current && retriesRef.current >= MAX_RETRIES) {
      return;
    }

    // Don't dial before there's a session to dial with. The org feed connects on
    // mount, where the single-agent feed used to be gated behind `/agents`
    // resolving — which implied auth had already happened. Without this the first
    // attempts 401, burn the retry budget, and the shell shows "Agent connection
    // lost" on a perfectly healthy system.
    if (!getWsToken()) {
      setConnectionState('connecting');
      timerRef.current = setTimeout(connect, 500);
      return;
    }

    setConnectionState('connecting');

    // In production, the API may be cross-origin (api.verso-cloud.io). Use
    // VITE_API_BASE if set; otherwise same-origin.
    const apiBase = import.meta.env.VITE_API_BASE || window.location.origin;
    const apiHost = new URL(apiBase).host;
    const wsBase = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${apiHost}`;
    const token = getWsToken();
    // No agent_id → the org feed.
    const scope = agentId ? `agent_id=${encodeURIComponent(agentId)}&` : '';
    const url = `${wsBase}/ws?${scope}${token ? `token=${encodeURIComponent(token)}` : ''}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnectionState('connected');
      retriesRef.current = 0;
      everConnectedRef.current = true;
      // No application-level ping/pong — WebSocket protocol keepalive is
      // handled by Cloudflare's edge. onclose detects drops.
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'agent.status') {
          setAgentStatus(data.status);
        } else if (data.type === 'host.status') {
          // Per-host, from the org feed.
          if (data.agent_id) {
            setHostStatus((prev) => (prev[data.agent_id] === data.status
              ? prev
              : { ...prev, [data.agent_id]: data.status }));
          }
          setLastEvent(data);
        } else if (data.type === 'org.hosts') {
          setHostStatus((prev) => {
            const next = { ...prev };
            for (const h of data.hosts || []) {
              if (h.agent_id && next[h.agent_id] === undefined) next[h.agent_id] = h.status;
            }
            return next;
          });
        } else {
          setLastEvent(data);
        }
      } catch {
        setLastEvent(event.data);
      }
    };

    ws.onclose = () => {
      setConnectionState('disconnected');
      const delay = Math.min(BASE_DELAY * 2 ** retriesRef.current, MAX_BACKOFF);
      retriesRef.current += 1;
      timerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [agentId]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(timerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { connected: connectionState === 'connected', connectionState, agentStatus, hostStatus, lastEvent };
}
